import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Plus, X, Send, Ban, FileText, Trash2, Search, ChevronLeft, Pencil, CheckCircle2, XCircle, ClipboardCheck, AlertTriangle, Copy, LineChart as LineChartIcon, ShieldAlert } from "lucide-react";
import ItemCombobox from "./ItemCombobox";
import POBenchmarkPanel from "./POBenchmarkPanel";
import ItemIntelligencePanel from "./ItemIntelligencePanel";
import ExportXlsxButton from "./ExportXlsxButton";
import POAmendmentModal from "./POAmendmentModal";
import { useOutlet } from "@/lib/outlet";

const STATUS_STYLES = {
  draft: "bg-[hsl(var(--rule))] text-[hsl(var(--ink-soft))]",
  pending_approval: "bg-[hsl(38_90%_88%)] text-[hsl(35_75%_38%)]",
  approved: "bg-[hsl(148_40%_88%)] text-[hsl(var(--forest))]",
  rejected: "bg-[hsl(6_60%_92%)] text-[hsl(6_62%_45%)]",
  sent: "bg-[hsl(var(--brass)/0.15)] text-[hsl(var(--brass-2))]",
  partially_received: "bg-[hsl(38_90%_88%)] text-[hsl(35_75%_38%)]",
  received: "bg-[hsl(148_40%_88%)] text-[hsl(var(--forest))]",
  cancelled: "bg-[hsl(6_60%_92%)] text-[hsl(6_62%_45%)]",
};

// A PO is editable while draft, or a `sent` PO with zero GRN receipts. Mirrors backend guard.
const canEditPO = (po) => {
  if (!po) return false;
  if (po.status === "draft") return true;
  if (po.status !== "sent") return false;
  return (po.lines || []).every((l) => Number(l.qty_received || 0) === 0);
};

// Admin escalation: shown when the normal edit path is closed AND the PO isn't cancelled.
const canAdminOverride = (po, isAdmin) => {
  if (!isAdmin || !po) return false;
  if (po.status === "cancelled") return false;
  return !canEditPO(po);
};

const fmtTs = (v) => {
  if (!v) return "";
  try { return new Date(v).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return String(v); }
};

export default function POsTab({ presetFilter } = {}) {
  const { user } = useAuth();
  // "isAdmin" here means "can approve" — either full admin or the limited
  // approver role (Ranjith). Approve/reject buttons + bulk approvals + PO
  // amendment button rely on this flag.
  const isAdmin = user?.role === "admin" || user?.role === "approver";
  const { outletParam } = useOutlet();
  const [pos, setPos] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  // Sync with the "Pending you" banner: when a preset filter is passed in, apply it once.
  useEffect(() => {
    if (presetFilter) setFilter(presetFilter);
  }, [presetFilter]);
  const [view, setView] = useState({ mode: "list" });
  const [rejectFor, setRejectFor] = useState(null); // {id, po_number}
  const [amendFor, setAmendFor] = useState(null);   // full PO object for amendment modal
  const [masterFor, setMasterFor] = useState(null); // PO object for master approve modal
  const [selected, setSelected] = useState({}); // {po_id: true}
  const selectedIds = useMemo(() => Object.keys(selected).filter((k) => selected[k]), [selected]);

  const load = async () => {
    setLoading(true);
    try {
      const [posRes, vendorsRes, itemsRes] = await Promise.all([
        api.get("/staff/purchase-orders"),
        api.get("/staff/vendors", { params: { active_only: true } }),
        api.get("/staff/inventory/items"),
      ]);
      setPos(posRes.data);
      setVendors(vendorsRes.data);
      setItems(itemsRes.data);
    } catch { toast.error("Could not load POs"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // v2 — outlet filter (soft): untagged rows match every outlet per user choice 2b.
  const outletFiltered = useMemo(() => {
    if (!outletParam) return pos;
    return pos.filter((p) => !p.outlet || p.outlet === outletParam);
  }, [pos, outletParam]);

  const pendingCount = useMemo(() => outletFiltered.filter((p) => p.status === "pending_approval").length, [outletFiltered]);
  const filtered = filter === "all" ? outletFiltered : outletFiltered.filter((p) => p.status === filter);

  const openNew = () => setView({ mode: "new" });
  const openEdit = (po) => setView({ mode: "edit", po });
  const openDetail = (po) => setView({ mode: "detail", po });
  const backToList = (reload = false) => {
    setView({ mode: "list" });
    if (reload) load();
  };

  const downloadPdf = async (po) => {
    try {
      const res = await api.get(`/staff/purchase-orders/${po.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `TBB-${(po.po_number || "PO").replace(/\//g, "-")}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const submit = async (po) => {
    if (!window.confirm(`Submit PO ${po.po_number} for admin approval?`)) return;
    try {
      await api.post(`/staff/purchase-orders/${po.id}/submit`);
      toast.success("Submitted for approval");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to submit"); }
  };
  const approve = async (po) => {
    if (!window.confirm(`Approve PO ${po.po_number} for ${po.vendor_snapshot?.name}?`)) return;
    try {
      await api.post(`/staff/purchase-orders/${po.id}/approve`);
      toast.success("PO approved");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to approve"); }
  };
  // Bulk approve — admin only. Only touches selected pending_approval POs; skips others.
  const bulkApprove = async () => {
    const targets = filtered.filter((p) => selected[p.id] && p.status === "pending_approval");
    if (!targets.length) { toast.error("No pending POs selected"); return; }
    if (!window.confirm(`Approve ${targets.length} purchase order${targets.length === 1 ? "" : "s"}?`)) return;
    let ok = 0, fail = 0;
    const errors = [];
    for (const po of targets) {
      try {
        await api.post(`/staff/purchase-orders/${po.id}/approve`);
        ok++;
      } catch (err) {
        fail++;
        errors.push(`${po.po_number}: ${err?.response?.data?.detail || "failed"}`);
      }
    }
    if (ok) toast.success(`${ok} PO${ok === 1 ? "" : "s"} approved`);
    if (fail) toast.error(`${fail} failed — ${errors[0]}${errors.length > 1 ? ` (+${errors.length - 1} more)` : ""}`);
    setSelected({});
    load();
  };
  const submitReject = async (reason) => {
    if (!rejectFor) return;
    try {
      await api.post(`/staff/purchase-orders/${rejectFor.id}/reject`, { reason });
      toast.success("PO rejected — sent back to draft");
      setRejectFor(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to reject"); }
  };

  const submitMasterApprove = async ({ reason }) => {
    if (!masterFor) return;
    try {
      await api.post(`/staff/purchase-orders/${masterFor.id}/master-approve`, { reason });
      toast.success(`Master-override approved · logged`);
      setMasterFor(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Master approval failed"); }
  };

  const duplicate = async (po) => {
    if (!window.confirm(`Duplicate ${po.po_number} to a new draft PO for ${po.vendor_snapshot?.name}?`)) return;
    try {
      const { data } = await api.post(`/staff/purchase-orders/${po.id}/duplicate`);
      toast.success(`Draft ${data.po_number} created — review and submit for approval`);
      load();
      setView({ mode: "edit", po: data });
    } catch (err) { toast.error(err?.response?.data?.detail || "Duplicate failed"); }
  };

  // Admin override editor: open the same POForm but flag the save as an override.
  const [adminEditFor, setAdminEditFor] = useState(null); // po object or null
  const openAdminEdit = (po) => setAdminEditFor(po);

  if (adminEditFor) {
    return (
      <AdminEditReasonPrompt
        po={adminEditFor}
        onCancel={() => setAdminEditFor(null)}
        onProceed={(reason) => {
          setAdminEditFor(null);
          setView({ mode: "edit", po: adminEditFor, overrideReason: reason });
        }}
      />
    );
  }

  if (view.mode === "new" || view.mode === "edit") {
    return <POForm vendors={vendors} items={items} initial={view.mode === "edit" ? view.po : null} overrideReason={view.mode === "edit" ? view.overrideReason : ""} onDone={() => backToList(true)} onCancel={() => backToList()} />;
  }
  if (view.mode === "detail") {
    return <PODetail po={view.po} isAdmin={isAdmin} onBack={() => backToList(true)} onDownload={downloadPdf} onEdit={openEdit} onSubmit={submit} onApprove={approve} onReject={() => setRejectFor({ id: view.po.id, po_number: view.po.po_number })} onDuplicate={duplicate} onAdminEdit={openAdminEdit} />;
  }

  return (
    <div data-testid="pos-tab">
      {isAdmin && pendingCount > 0 && filter !== "pending_approval" && (
        <div className="mb-4 flex items-center gap-3 p-3 border border-[hsl(var(--brass))] bg-[hsl(var(--brass)/0.1)] text-sm">
          <ClipboardCheck size={16} className="text-[hsl(var(--brass-2))]" />
          <span>
            <b>{pendingCount}</b> purchase order{pendingCount === 1 ? "" : "s"} awaiting your approval.
          </span>
          <button data-testid="po-goto-pending" onClick={() => setFilter("pending_approval")} className="ml-auto text-xs uppercase tracking-wider text-[hsl(var(--brass-2))] hover:underline">Review →</button>
        </div>
      )}

      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="inline-flex flex-wrap gap-1 border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))] rounded-full p-1">
          {["all", "draft", "pending_approval", "approved", "rejected", "sent", "partially_received", "received", "cancelled"].map((f) => (
            <button
              key={f}
              data-testid={`po-filter-${f}`}
              onClick={() => setFilter(f)}
              className={`px-3 py-1 text-[11px] uppercase tracking-wider rounded-full ${filter === f ? "bg-[hsl(var(--forest))] text-[hsl(var(--bg))]" : "text-[hsl(var(--ink-soft))]"}`}
            >
              {f.replace(/_/g, " ")}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && selectedIds.length > 0 && (
            <button
              data-testid="po-bulk-approve"
              onClick={bulkApprove}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded bg-[hsl(var(--forest))] text-[hsl(var(--bg))]"
            >
              <CheckCircle2 size={13} /> Approve {selectedIds.length} selected
            </button>
          )}
          <ExportXlsxButton
            url="/staff/exports/purchase-orders.xlsx"
            params={{
              ...(outletParam ? { outlet: outletParam } : {}),
              ...(filter !== "all" ? { status: filter } : {}),
            }}
            testId="po-export-xlsx"
            label="Export .xlsx"
          />
          <button data-testid="po-new" onClick={openNew} className="btn-brass !py-2 !px-4 text-sm">
            <Plus size={14} /> New PO
          </button>
        </div>
      </div>

      <div className="mt-6 border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))]">
        {loading && <div className="p-6 text-[hsl(var(--ink-soft))]">Loading...</div>}
        {!loading && filtered.length === 0 && (
          <div data-testid="pos-empty" className="p-10 text-center text-[hsl(var(--ink-soft))]">
            <FileText className="mx-auto mb-2 opacity-40" size={28} />
            No purchase orders in this view.
          </div>
        )}
        {!loading && filtered.length > 0 && (
          <>
            {isAdmin && filter === "pending_approval" && filtered.length > 0 && (
              <div className="px-4 py-2 border-b border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))] text-xs text-[hsl(var(--ink-soft))] flex items-center gap-2">
                <input
                  type="checkbox"
                  data-testid="po-select-all"
                  className="h-4 w-4 accent-[hsl(var(--forest))]"
                  checked={selectedIds.length === filtered.length && filtered.length > 0}
                  onChange={(e) => {
                    if (e.target.checked) setSelected(Object.fromEntries(filtered.map((p) => [p.id, true])));
                    else setSelected({});
                  }}
                />
                <span>Select all pending ({filtered.length})</span>
              </div>
            )}
            <ul className="divide-y divide-[hsl(var(--rule))]">
            {filtered.map((po) => (
              <li key={po.id} data-testid={`po-${po.id}`} className="p-4 grid grid-cols-1 md:grid-cols-[auto_1fr_auto] gap-3 items-start">
                <div className="pt-1">
                  {isAdmin && po.status === "pending_approval" ? (
                    <input
                      type="checkbox"
                      data-testid={`po-select-${po.id}`}
                      checked={!!selected[po.id]}
                      onChange={(e) => setSelected({ ...selected, [po.id]: e.target.checked })}
                      className="h-4 w-4 accent-[hsl(var(--forest))]"
                      aria-label={`Select ${po.po_number}`}
                    />
                  ) : <div className="w-4" />}
                </div>
                <div>
                  <div className="flex items-baseline gap-3 flex-wrap">
                    <div className="font-mono text-sm text-[hsl(var(--brass-2))]">{po.po_number}</div>
                    <span data-testid={`po-status-${po.id}`} className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${STATUS_STYLES[po.status] || ""}`}>{po.status.replace(/_/g, " ")}</span>
                    <div className="text-sm font-semibold">{po.vendor_snapshot?.name || "—"}</div>
                    <div className="text-xs text-[hsl(var(--ink-soft))]">
                      {(po.lines || []).length} lines · <span className="font-semibold">₹{Number(po.total).toLocaleString("en-IN")}</span>
                    </div>
                  </div>
                  <div className="mt-1 text-xs text-[hsl(var(--ink-soft))]">
                    Created {fmtTs(po.created_at)}
                    {po.expected_delivery_date && <> · Expected: {po.expected_delivery_date}</>}
                    {po.created_by && <> · by {po.created_by}</>}
                    {po.submitted_at && <> · Submitted {fmtTs(po.submitted_at)}</>}
                    {po.approved_at && <> · Approved {fmtTs(po.approved_at)}{po.approved_by ? ` by ${po.approved_by}` : ""}</>}
                  </div>
                  {po.rejection_reason && po.status === "draft" && (
                    <div data-testid={`po-reject-reason-${po.id}`} className="mt-2 inline-flex items-start gap-2 text-xs px-2.5 py-1.5 rounded bg-[hsl(6_60%_95%)] text-[hsl(6_62%_38%)] border border-[hsl(6_62%_45%_/_0.3)]">
                      <AlertTriangle size={12} className="mt-0.5" />
                      <span><b>Rejected — please revise:</b> {po.rejection_reason}</span>
                    </div>
                  )}
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button data-testid={`po-view-${po.id}`} onClick={() => openDetail(po)} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded border border-[hsl(var(--rule))]">
                    <Search size={12} /> View
                  </button>
                  {isAdmin && ["approved", "sent", "partially_received"].includes(po.status) && (
                    <button
                      data-testid={`po-amend-${po.id}`}
                      onClick={() => setAmendFor(po)}
                      title="Raise a PO amendment — updates lines / total with an audit-logged reason. Use when goods received exceed the authorised PO."
                      className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded border border-[hsl(35_75%_38%)] text-[hsl(35_75%_38%)] hover:bg-[hsl(35_75%_38%)] hover:text-white transition-colors"
                    >
                      <FileText size={12} /> Amend
                    </button>
                  )}
                  {po.status === "draft" && (
                    <button data-testid={`po-submit-${po.id}`} onClick={() => submit(po)} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded btn-brass">
                      <ClipboardCheck size={12} /> Submit for approval
                    </button>
                  )}
                  {po.status === "pending_approval" && isAdmin && (
                    <>
                      <button data-testid={`po-approve-${po.id}`} onClick={() => approve(po)} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded bg-[hsl(var(--forest))] text-[hsl(var(--bg))]">
                        <CheckCircle2 size={12} /> Approve
                      </button>
                      <button data-testid={`po-reject-${po.id}`} onClick={() => setRejectFor({ id: po.id, po_number: po.po_number })} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded border border-[hsl(6_62%_45%)] text-[hsl(6_62%_45%)]">
                        <XCircle size={12} /> Reject
                      </button>
                      <button data-testid={`po-master-approve-${po.id}`} onClick={() => setMasterFor(po)} title="Admin master override — approves this PO without going through submit. Logged." className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded border border-[hsl(6_62%_45%)] text-[hsl(6_62%_45%)] hover:bg-[hsl(6_62%_45%)] hover:text-white transition-colors">
                        <ShieldAlert size={12} /> Master approve
                      </button>
                    </>
                  )}
                  {po.status === "draft" && isAdmin && (
                    <button data-testid={`po-master-approve-${po.id}`} onClick={() => setMasterFor(po)} title="Admin master override — approves this draft PO without needing a separate submitter (bypasses segregation). Logged." className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded border border-[hsl(6_62%_45%)] text-[hsl(6_62%_45%)] hover:bg-[hsl(6_62%_45%)] hover:text-white transition-colors">
                      <ShieldAlert size={12} /> Master approve
                    </button>
                  )}
                  {canEditPO(po) && (
                    <button data-testid={`po-edit-${po.id}`} onClick={() => openEdit(po)} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded border border-[hsl(var(--ink))] text-[hsl(var(--ink))] hover:bg-[hsl(var(--ink))] hover:text-[hsl(var(--bg))] transition-colors">
                      <Pencil size={12} /> Edit
                    </button>
                  )}
                  {canAdminOverride(po, isAdmin) && (
                    <button data-testid={`po-admin-edit-${po.id}`} onClick={() => openAdminEdit(po)} title="Admin edit — requires a reason (logged)" className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded border border-[hsl(6_62%_45%)] text-[hsl(6_62%_45%)] hover:bg-[hsl(6_62%_45%)] hover:text-white transition-colors">
                      <AlertTriangle size={12} /> Admin edit
                    </button>
                  )}
                  <button data-testid={`po-duplicate-${po.id}`} onClick={() => duplicate(po)} title="Create a fresh draft from this PO" className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded border border-[hsl(var(--brass))] text-[hsl(var(--brass-2))] hover:bg-[hsl(var(--brass))] hover:text-[hsl(var(--bg))] transition-colors">
                    <Copy size={12} /> Duplicate
                  </button>
                  <button data-testid={`po-pdf-${po.id}`} onClick={() => downloadPdf(po)} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded border border-[hsl(var(--forest))] text-[hsl(var(--forest))]">
                    <FileText size={12} /> PDF
                  </button>
                </div>
              </li>
            ))}
          </ul>
          </>
        )}
      </div>

      {rejectFor && (
        <RejectModal target={`PO ${rejectFor.po_number}`} onCancel={() => setRejectFor(null)} onSubmit={submitReject} testidPrefix="po-reject" />
      )}
      {amendFor && (
        <POAmendmentModal
          po={amendFor}
          onCancel={() => setAmendFor(null)}
          onSaved={() => { setAmendFor(null); load(); }}
        />
      )}
      {masterFor && (
        <POMasterApproveModal
          po={masterFor}
          onCancel={() => setMasterFor(null)}
          onSubmit={submitMasterApprove}
        />
      )}
    </div>
  );
}

// ----------- PO Master Approve modal (mandatory audit reason) -----------
function POMasterApproveModal({ po, onCancel, onSubmit }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (reason.trim().length < 5) { toast.error("Reason must be at least 5 characters"); return; }
    setBusy(true);
    try { await onSubmit({ reason: reason.trim() }); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="po-master-modal" className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
         onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="bg-[hsl(var(--bg))] border border-[hsl(6_62%_45%)] w-full max-w-lg p-6 shadow-xl">
        <div className="flex items-start gap-3">
          <ShieldAlert size={20} className="text-[hsl(6_62%_45%)] shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="eyebrow text-[hsl(6_62%_45%)]">Admin master override</div>
            <h2 className="mt-1 display text-xl">Force-approve {po.po_number}</h2>
            <p className="mt-2 text-xs text-[hsl(var(--ink-soft))] leading-relaxed">
              This bypasses the normal <b>submit → approve</b> round-trip. Current status: <b>{po.status.replace(/_/g, " ")}</b> · vendor <b>{po.vendor_snapshot?.name || "—"}</b> · total ₹{Number(po.total).toLocaleString("en-IN")}.
              The action is <b>permanently logged</b> in the audit trail with the reason below. Use for urgent authorisations only.
            </p>
          </div>
          <button onClick={onCancel} className="text-[hsl(var(--ink-soft))]"><X size={16} /></button>
        </div>

        <div className="mt-5">
          <label className="block">
            <span className="block text-[10px] font-semibold uppercase tracking-wider text-[hsl(var(--ink-soft))] mb-1">
              Reason for override * (min 5 chars)
            </span>
            <textarea
              data-testid="po-master-reason"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Ranjith out of office; vendor delivery cannot wait — approving directly."
              className="tbb-inp w-full resize-none text-sm"
              autoFocus
            />
          </label>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onCancel} className="text-sm text-[hsl(var(--ink-soft))] px-3">Cancel</button>
          <button
            data-testid="po-master-submit"
            onClick={submit}
            disabled={busy || reason.trim().length < 5}
            className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded bg-[hsl(6_62%_45%)] text-white disabled:opacity-50"
          >
            <ShieldAlert size={13} /> {busy ? "Approving…" : "Force approve"}
          </button>
        </div>
        <style>{`.tbb-inp{background:hsl(var(--bg));border:1px solid hsl(var(--rule));padding:0.55rem 0.75rem;font-size:0.9rem}.tbb-inp:focus{outline:1px solid hsl(var(--forest));border-color:hsl(var(--forest))}`}</style>
      </div>
    </div>
  );
}

// ----------- Admin edit reason prompt (gate to override editing) -----------
function AdminEditReasonPrompt({ po, onCancel, onProceed }) {
  const [reason, setReason] = useState("");
  return (
    <div data-testid="po-admin-edit-prompt">
      <button onClick={onCancel} className="inline-flex items-center gap-1 text-sm text-[hsl(var(--ink-soft))] mb-4">
        <ChevronLeft size={14} /> Back
      </button>
      <div className="max-w-xl p-6 border border-[hsl(var(--brass))] bg-[hsl(var(--brass)/0.05)]">
        <div className="flex items-start gap-3">
          <AlertTriangle size={22} className="text-[hsl(var(--brass-2))] mt-0.5 shrink-0" />
          <div>
            <h2 className="text-xl font-semibold">Admin edit · {po.po_number}</h2>
            <p className="text-xs text-[hsl(var(--ink-soft))] mt-1">Current status: <b>{po.status.replace(/_/g, " ")}</b>. Editing outside the normal window bypasses the approval guard. Enter a reason — this is logged to the PO's audit trail.</p>
          </div>
        </div>
        <textarea
          data-testid="po-admin-edit-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={4}
          placeholder="e.g. Vendor called with revised pricing after PO was already received in warehouse."
          className="mt-4 w-full border border-[hsl(var(--rule))] p-2.5 text-sm bg-[hsl(var(--bg))]"
        />
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onCancel} className="text-sm text-[hsl(var(--ink-soft))] px-3">Cancel</button>
          <button
            data-testid="po-admin-edit-proceed"
            disabled={!reason.trim()}
            onClick={() => onProceed(reason.trim())}
            className="btn-brass !py-2 !px-4 text-sm disabled:opacity-40"
          >
            Continue to edit →
          </button>
        </div>
      </div>
    </div>
  );
}


// ----------- Rejection modal (shared for POs + GRNs elsewhere) -----------
export function RejectModal({ target, onCancel, onSubmit, testidPrefix = "reject" }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    const r = reason.trim();
    if (!r) { toast.error("Rejection reason required"); return; }
    setBusy(true);
    try { await onSubmit(r); } finally { setBusy(false); }
  };
  return (
    <div data-testid={`${testidPrefix}-modal`} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onCancel}>
      <div className="bg-[hsl(var(--bg))] border border-[hsl(var(--rule))] max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3">
          <XCircle size={20} className="text-[hsl(6_62%_45%)] mt-0.5" />
          <div>
            <h3 className="text-lg font-semibold">Reject {target}</h3>
            <p className="text-xs text-[hsl(var(--ink-soft))] mt-0.5">The submitter will see this reason and can revise & resubmit.</p>
          </div>
        </div>
        <textarea
          data-testid={`${testidPrefix}-reason`}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={4}
          placeholder="e.g. Rate for cauliflower is 40% above market. Please revise."
          className="mt-4 w-full border border-[hsl(var(--rule))] p-2.5 text-sm bg-[hsl(var(--bg))]"
        />
        <div className="mt-4 flex justify-end gap-2">
          <button data-testid={`${testidPrefix}-cancel`} onClick={onCancel} className="text-sm text-[hsl(var(--ink-soft))] px-3">Cancel</button>
          <button data-testid={`${testidPrefix}-confirm`} disabled={busy || !reason.trim()} onClick={send} className="text-sm px-4 py-2 rounded bg-[hsl(6_62%_45%)] text-white disabled:opacity-40">
            {busy ? "Rejecting…" : "Reject"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ----------- PO Detail (view + workflow actions) -----------
function PODetail({ po: initialPo, isAdmin, onBack, onDownload, onEdit, onSubmit, onApprove, onReject, onDuplicate, onAdminEdit }) {
  const [po, setPo] = useState(initialPo);
  const [busy, setBusy] = useState(false);
  const canSend = po.status === "approved";
  const canCancel = !["cancelled", "received", "rejected"].includes(po.status);
  const editable = canEditPO(po);
  const send = async () => {
    if (!window.confirm(`Mark PO ${po.po_number} as sent to ${po.vendor_snapshot?.name}?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/staff/purchase-orders/${po.id}/send`);
      setPo(data);
      toast.success("PO sent");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const cancel = async () => {
    if (!window.confirm(`Cancel PO ${po.po_number}? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/staff/purchase-orders/${po.id}/cancel`);
      setPo(data);
      toast.success("PO cancelled");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="po-detail">
      <button data-testid="po-back" onClick={onBack} className="inline-flex items-center gap-1 text-sm text-[hsl(var(--ink-soft))] mb-4">
        <ChevronLeft size={14} /> Back to POs
      </button>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="font-mono text-[hsl(var(--brass-2))]">{po.po_number}</div>
          <h2 className="text-2xl mt-1">{po.vendor_snapshot?.name}</h2>
          <div className="text-xs text-[hsl(var(--ink-soft))] mt-1">
            {po.vendor_snapshot?.phone} {po.vendor_snapshot?.gstin && <>· GSTIN {po.vendor_snapshot.gstin}</>}
          </div>
          {po.rejection_reason && po.status === "draft" && (
            <div data-testid="po-detail-reject-reason" className="mt-3 inline-flex items-start gap-2 text-xs px-2.5 py-1.5 rounded bg-[hsl(6_60%_95%)] text-[hsl(6_62%_38%)] border border-[hsl(6_62%_45%_/_0.3)]">
              <AlertTriangle size={12} className="mt-0.5" />
              <span><b>Previously rejected — please revise:</b> {po.rejection_reason}</span>
            </div>
          )}
          <div className="mt-3 text-[11px] text-[hsl(var(--ink-soft))] flex flex-wrap gap-x-4 gap-y-0.5">
            {po.created_at && <span>Created {fmtTs(po.created_at)}{po.created_by ? ` by ${po.created_by}` : ""}</span>}
            {po.submitted_at && <span>· Submitted {fmtTs(po.submitted_at)}{po.submitted_by ? ` by ${po.submitted_by}` : ""}</span>}
            {po.approved_at && <span>· Approved {fmtTs(po.approved_at)}{po.approved_by ? ` by ${po.approved_by}` : ""}</span>}
            {po.rejected_at && <span>· Rejected {fmtTs(po.rejected_at)}{po.rejected_by ? ` by ${po.rejected_by}` : ""}</span>}
            {po.sent_at && <span>· Sent {fmtTs(po.sent_at)}</span>}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded self-center ${STATUS_STYLES[po.status] || ""}`}>{po.status.replace(/_/g, " ")}</span>
          {po.status === "draft" && (
            <button data-testid="po-detail-submit" onClick={() => onSubmit(po)} className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded btn-brass">
              <ClipboardCheck size={12} /> Submit for approval
            </button>
          )}
          {po.status === "pending_approval" && isAdmin && (
            <>
              <button data-testid="po-detail-approve" onClick={() => onApprove(po)} disabled={busy} className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded bg-[hsl(var(--forest))] text-[hsl(var(--bg))]">
                <CheckCircle2 size={12} /> Approve
              </button>
              <button data-testid="po-detail-reject" onClick={onReject} disabled={busy} className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded border border-[hsl(6_62%_45%)] text-[hsl(6_62%_45%)]">
                <XCircle size={12} /> Reject
              </button>
            </>
          )}
          {canSend && (
            <button data-testid="po-send" onClick={send} disabled={busy} className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded btn-brass">
              <Send size={12} /> Send to vendor
            </button>
          )}
          {editable && onEdit && (
            <button data-testid="po-detail-edit" onClick={() => onEdit(po)} className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded border border-[hsl(var(--ink))] text-[hsl(var(--ink))] hover:bg-[hsl(var(--ink))] hover:text-[hsl(var(--bg))] transition-colors">
              <Pencil size={12} /> Edit PO
            </button>
          )}
          {canAdminOverride(po, isAdmin) && onAdminEdit && (
            <button data-testid="po-detail-admin-edit" onClick={() => onAdminEdit(po)} title="Admin edit — requires a reason (logged)" className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded border border-[hsl(6_62%_45%)] text-[hsl(6_62%_45%)] hover:bg-[hsl(6_62%_45%)] hover:text-white transition-colors">
              <AlertTriangle size={12} /> Admin edit
            </button>
          )}
          <button onClick={() => onDownload(po)} className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded border border-[hsl(var(--forest))] text-[hsl(var(--forest))]">
            <FileText size={12} /> Download PDF
          </button>
          {onDuplicate && (
            <button data-testid="po-detail-duplicate" onClick={() => onDuplicate(po)} className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded border border-[hsl(var(--brass))] text-[hsl(var(--brass-2))] hover:bg-[hsl(var(--brass))] hover:text-[hsl(var(--bg))] transition-colors">
              <Copy size={12} /> Duplicate
            </button>
          )}
          {canCancel && (
            <button data-testid="po-cancel" onClick={cancel} disabled={busy} className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded border border-[hsl(6_62%_45%)] text-[hsl(6_62%_45%)]">
              <Ban size={12} /> Cancel PO
            </button>
          )}
        </div>
      </div>

      <div className="mt-6 border border-[hsl(var(--rule))]">
        <table className="w-full text-sm">
          <thead className="bg-[hsl(var(--bg-2))] text-[hsl(var(--ink-soft))] text-xs uppercase tracking-wider sticky top-0 z-10 shadow-[0_1px_0_hsl(var(--rule))]">
            <tr>
              <th className="text-left p-2">#</th>
              <th className="text-left p-2">Item</th>
              <th className="text-left p-2">Unit</th>
              <th className="text-right p-2">Ordered</th>
              <th className="text-right p-2">Received</th>
              <th className="text-right p-2">Rate</th>
              <th className="text-right p-2">Amount</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[hsl(var(--rule))]">
            {(po.lines || []).map((l, idx) => (
              <tr key={idx}>
                <td className="p-2 text-[hsl(var(--ink-soft))]">{idx + 1}</td>
                <td className="p-2">{l.item_name}<div className="text-xs text-[hsl(var(--ink-soft))]">{l.category}</div></td>
                <td className="p-2">{l.unit}</td>
                <td className="p-2 text-right">{Number(l.qty_ordered).toLocaleString("en-IN")}</td>
                <td className="p-2 text-right">
                  <span className={Number(l.qty_received) >= Number(l.qty_ordered) ? "text-[hsl(var(--forest))]" : Number(l.qty_received) > 0 ? "text-[hsl(35_75%_38%)]" : "text-[hsl(var(--ink-soft))]"}>
                    {Number(l.qty_received).toLocaleString("en-IN")}
                  </span>
                </td>
                <td className="p-2 text-right">₹{Number(l.rate).toLocaleString("en-IN")}</td>
                <td className="p-2 text-right">₹{Number(l.amount || l.qty_ordered * l.rate).toLocaleString("en-IN")}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-[hsl(var(--bg-2))]">
            <tr><td className="p-2 text-right text-xs" colSpan={6}>Subtotal</td><td className="p-2 text-right">₹{Number(po.subtotal).toLocaleString("en-IN")}</td></tr>
            <tr><td className="p-2 text-right text-xs" colSpan={6}>Tax ({po.tax_percent}%)</td><td className="p-2 text-right">₹{Number(po.tax_amount).toLocaleString("en-IN")}</td></tr>
            <tr><td className="p-2 text-right text-sm font-semibold" colSpan={6}>Total</td><td className="p-2 text-right font-semibold">₹{Number(po.total).toLocaleString("en-IN")}</td></tr>
          </tfoot>
        </table>
      </div>

      {po.notes && (
        <div className="mt-4">
          <div className="text-xs uppercase tracking-wider text-[hsl(var(--brass-2))] mb-1">Notes</div>
          <div className="text-sm text-[hsl(var(--ink-soft))]">{po.notes}</div>
        </div>
      )}

      <POBenchmarkPanel poId={po.id} editable={isAdmin} />

      {po.admin_edits && po.admin_edits.length > 0 && (
        <div data-testid="po-admin-edits" className="mt-6 p-4 border border-[hsl(6_62%_45%_/_0.4)] bg-[hsl(6_60%_97%)]">
          <div className="text-xs uppercase tracking-wider text-[hsl(6_62%_45%)] font-semibold mb-2 flex items-center gap-1.5"><AlertTriangle size={12} /> Admin override log</div>
          <ul className="space-y-2 text-sm">
            {po.admin_edits.slice().reverse().map((e, i) => (
              <li key={i} className="text-xs text-[hsl(var(--ink))]">
                <span className="text-[hsl(var(--ink-soft))]">
                  {new Date(e.at).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                  {" · "}{e.by}
                  {e.from_status && <> · from status <b>{e.from_status.replace(/_/g, " ")}</b></>}
                </span>
                <div className="mt-0.5">{e.reason}</div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ----------- PO Create/Edit form -----------
function POForm({ vendors, items, initial, overrideReason, onDone, onCancel }) {
  const { outletParam } = useOutlet();
  const [vendorId, setVendorId] = useState(initial?.vendor_id || "");
  const [tax, setTax] = useState(initial?.tax_percent || 0);
  const [expected, setExpected] = useState(initial?.expected_delivery_date || "");
  const [notes, setNotes] = useState(initial?.notes || "");
  const [lines, setLines] = useState(
    (initial?.lines || []).map((l) => ({
      item_id: l.item_id || "",
      item_name: l.item_name,
      category: l.category || "",
      unit: l.unit || "kg",
      qty_ordered: l.qty_ordered || 1,
      rate: l.rate || 0,
    })),
  );
  const [saving, setSaving] = useState(false);
  const [intelLine, setIntelLine] = useState(null);

  const editingSent = initial && initial.status === "sent";

  const addLine = () => setLines([...lines, { item_id: "", item_name: "", category: "", unit: "kg", qty_ordered: 1, rate: 0 }]);
  const removeLine = (i) => setLines(lines.filter((_, idx) => idx !== i));
  const updateLine = (i, patch) => setLines(lines.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));

  const pickItem = (i, itemId) => {
    const it = items.find((x) => x.id === itemId);
    if (!it) { updateLine(i, { item_id: "", item_name: "" }); return; }
    updateLine(i, { item_id: it.id, item_name: it.name, category: it.category || "", unit: it.unit || "kg", rate: it.last_rate || 0 });
  };

  const subtotal = lines.reduce((s, l) => s + Number(l.qty_ordered || 0) * Number(l.rate || 0), 0);
  const taxAmt = subtotal * (Number(tax) || 0) / 100;
  const total = subtotal + taxAmt;

  const save = async () => {
    if (!vendorId) return toast.error("Select a vendor");
    if (!lines.length) return toast.error("Add at least one line");
    for (const l of lines) {
      if (!l.item_name.trim() || Number(l.qty_ordered) <= 0) return toast.error("Invalid line — item name and qty > 0 required");
    }
    setSaving(true);
    try {
      const payload = { vendor_id: vendorId, tax_percent: Number(tax) || 0, expected_delivery_date: expected || "", notes: notes || "", lines };
      // v2 — tag the PO with the active outlet when creating (soft filter downstream).
      if (!initial && outletParam) payload.outlet = outletParam;
      if (overrideReason) {
        payload.admin_override = true;
        payload.override_reason = overrideReason;
      }
      if (initial) {
        const { data } = await api.patch(`/staff/purchase-orders/${initial.id}`, payload);
        toast.success(overrideReason ? `PO ${data.po_number} edited (admin override logged)` : `PO ${data.po_number} saved`);
      } else {
        const { data } = await api.post("/staff/purchase-orders", payload);
        toast.success(`PO ${data.po_number} created — submit for approval to send`);
      }
      onDone();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="po-form">
      <button onClick={onCancel} className="inline-flex items-center gap-1 text-sm text-[hsl(var(--ink-soft))] mb-4">
        <ChevronLeft size={14} /> Back
      </button>
      <h2 className="text-2xl">{overrideReason ? `Admin edit · ${initial.po_number}` : (initial ? `Edit PO ${initial.po_number}` : "New Purchase Order")}</h2>
      {overrideReason && (
        <div data-testid="po-override-banner" className="mt-2 p-3 border border-[hsl(var(--brass))] bg-[hsl(var(--brass)/0.1)] text-xs text-[hsl(var(--brass-2))]">
          <b>Admin override enabled</b> — this edit will bypass the normal status guard and be recorded to the audit trail. Reason: <i>{overrideReason}</i>
        </div>
      )}
      {!overrideReason && (
        <p className="text-xs text-[hsl(var(--ink-soft))] mt-1">After saving, submit for admin approval. Only approved POs can be sent to the vendor.</p>
      )}

      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
          Vendor *
          <select data-testid="po-vendor" value={vendorId} onChange={(e) => setVendorId(e.target.value)} disabled={!!initial} className="mt-1 block w-full border border-[hsl(var(--rule))] p-2.5 text-sm bg-[hsl(var(--bg))] disabled:opacity-60">
            <option value="">— Select vendor —</option>
            {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
        </label>
        <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
          Expected delivery
          <input data-testid="po-expected" type="date" value={expected} onChange={(e) => setExpected(e.target.value)} className="mt-1 block w-full border border-[hsl(var(--rule))] p-2.5 text-sm bg-[hsl(var(--bg))]" />
        </label>
        <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
          Tax %
          <input data-testid="po-tax" type="number" step="0.01" value={tax} onChange={(e) => setTax(e.target.value)} className="mt-1 block w-full border border-[hsl(var(--rule))] p-2.5 text-sm bg-[hsl(var(--bg))]" />
        </label>
      </div>

      <div className="mt-6">
        <div className="flex items-center justify-between">
          <div className="eyebrow">Line items</div>
          <button data-testid="po-add-line" type="button" onClick={addLine} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded border border-[hsl(var(--rule))]">
            <Plus size={12} /> Add line
          </button>
        </div>
        <div className="mt-2 border border-[hsl(var(--rule))]">
          <table className="w-full text-sm">
            <thead className="bg-[hsl(var(--bg-2))] text-[hsl(var(--ink-soft))] text-xs uppercase tracking-wider sticky top-0 z-10 shadow-[0_1px_0_hsl(var(--rule))]">
              <tr>
                <th className="text-left p-2">Item</th>
                <th className="text-left p-2 w-24">Unit</th>
                <th className="text-right p-2 w-24">Qty</th>
                <th className="text-right p-2 w-28">Rate</th>
                <th className="text-right p-2 w-28">Amount</th>
                <th className="p-2 w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[hsl(var(--rule))]">
              {lines.map((l, i) => {
                const catItem = items.find((it) => it.id === l.item_id);
                const lastRate = catItem ? Number(catItem.last_rate || 0) : 0;
                const currentRate = Number(l.rate || 0);
                const variance = lastRate > 0 ? ((currentRate - lastRate) / lastRate) * 100 : 0;
                const varianceWarn = lastRate > 0 && Math.abs(variance) >= 10;
                return (
                <tr key={i} data-testid={`po-line-${i}`}>
                  <td className="p-2 align-top">
                    <ItemCombobox
                      items={items}
                      value={{ item_id: l.item_id, item_name: l.item_name }}
                      inputTestId={`po-line-item-${i}`}
                      onPick={(it) => updateLine(i, { item_id: it.id, item_name: it.name, category: it.category || "", unit: it.unit || "kg", rate: l.rate || it.last_rate || 0 })}
                      onFreeText={(text) => updateLine(i, { item_id: "", item_name: text })}
                    />
                    {l.item_name && (
                      <button
                        type="button"
                        data-testid={`po-line-intel-${i}`}
                        onClick={() => setIntelLine({ item_id: l.item_id || null, item_name: l.item_name, unit: l.unit || "kg" })}
                        className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[hsl(var(--brass-2))] hover:text-[hsl(var(--forest))]"
                        title="Open price intelligence for this item"
                      >
                        <LineChartIcon size={10} /> Price intel
                      </button>
                    )}
                  </td>
                  <td className="p-2 align-top"><input value={l.unit} onChange={(e) => updateLine(i, { unit: e.target.value })} className="block w-full border border-[hsl(var(--rule))] p-1.5 text-xs bg-[hsl(var(--bg))]" /></td>
                  <td className="p-2 text-right align-top"><input type="number" step="0.01" value={l.qty_ordered} onChange={(e) => updateLine(i, { qty_ordered: e.target.value })} className="block w-full text-right border border-[hsl(var(--rule))] p-1.5 text-xs bg-[hsl(var(--bg))]" /></td>
                  <td className="p-2 text-right align-top">
                    <input type="number" step="0.01" value={l.rate} onChange={(e) => updateLine(i, { rate: e.target.value })} className={`block w-full text-right border p-1.5 text-xs bg-[hsl(var(--bg))] ${varianceWarn ? "border-[hsl(6_62%_45%)] bg-[hsl(6_60%_97%)]" : "border-[hsl(var(--rule))]"}`} />
                    {varianceWarn && (
                      <div data-testid={`po-rate-variance-${i}`} className="mt-0.5 text-[10px] text-[hsl(6_62%_45%)] font-semibold">
                        {variance > 0 ? "+" : ""}{variance.toFixed(1)}% vs last (₹{lastRate.toLocaleString("en-IN")})
                      </div>
                    )}
                  </td>
                  <td className="p-2 text-right align-top">₹{(Number(l.qty_ordered || 0) * Number(l.rate || 0)).toLocaleString("en-IN")}</td>
                  <td className="p-2 text-right align-top"><button type="button" onClick={() => removeLine(i)} className="text-[hsl(6_62%_45%)]"><Trash2 size={14} /></button></td>
                </tr>
                );
              })}
              {lines.length === 0 && (
                <tr><td colSpan={6} className="p-4 text-center text-xs text-[hsl(var(--ink-soft))]">No lines yet. Click Add line.</td></tr>
              )}
            </tbody>
            <tfoot className="bg-[hsl(var(--bg-2))]">
              <tr><td colSpan={4} className="p-2 text-right text-xs">Subtotal</td><td className="p-2 text-right">₹{subtotal.toLocaleString("en-IN")}</td><td /></tr>
              <tr><td colSpan={4} className="p-2 text-right text-xs">Tax ({tax}%)</td><td className="p-2 text-right">₹{taxAmt.toLocaleString("en-IN")}</td><td /></tr>
              <tr><td colSpan={4} className="p-2 text-right text-sm font-semibold">Total</td><td className="p-2 text-right font-semibold">₹{total.toLocaleString("en-IN")}</td><td /></tr>
            </tfoot>
          </table>
        </div>
      </div>

      <div className="mt-4">
        <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">Notes</label>
        <textarea data-testid="po-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="mt-1 block w-full border border-[hsl(var(--rule))] p-2 text-sm bg-[hsl(var(--bg))]" />
      </div>

      <div className="mt-6 flex justify-end gap-3">
        <button type="button" onClick={onCancel} className="text-sm text-[hsl(var(--ink-soft))] px-3">Cancel</button>
        <button type="button" data-testid="po-save" disabled={saving} onClick={save} className="btn-brass !py-2.5 !px-5 text-sm">
          {saving ? "Saving..." : (editingSent ? "Save changes" : (initial ? "Save PO" : "Save PO"))}
        </button>
      </div>

      {intelLine && (
        <ItemIntelligencePanel
          itemName={intelLine.item_name}
          itemId={intelLine.item_id}
          unit={intelLine.unit}
          onClose={() => setIntelLine(null)}
        />
      )}
    </div>
  );
}
